import React from "react";

/**
 * Shimmer placeholder block. Styling lives in pages/Layouts/index.css (.at-skeleton).
 * Props: width, height, radius, className, style
 */
export const Skeleton = ({ width = "100%", height = 16, radius = 4, className = "", style = {} }) => (
  <span
    className={`at-skeleton d-block ${className}`}
    aria-hidden="true"
    style={{ width, height, borderRadius: radius, ...style }}
  />
);

/** A block of stacked text lines. */
export const SkeletonText = ({ lines = 3, width = "100%", className = "" }) => (
  <span className={`d-block ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        width={i === lines - 1 ? "60%" : width}
        height={12}
        className="mb-2"
      />
    ))}
  </span>
);

/** Table body rows placeholder — drop-in for a <tbody>. */
export const SkeletonRows = ({ cols = 4, rows = 6 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r}>
        {Array.from({ length: cols }).map((_, c) => (
          <td key={c}>
            <Skeleton height={12} width={c === 0 ? "80%" : "60%"} />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// Banner + barra de separadores, igual ao topo das páginas de serviço
const HeroAndTabs = ({ tabs = 2 }) => (
  <>
    <Skeleton height={200} radius={0} />
    {tabs > 0 && (
      <div className="at-page-landing" style={{ paddingBottom: 0 }}>
        <div className="at-card card at-tabs-bar" style={{ minHeight: 50 }}>
          <div className="d-flex gap-4 py-3">
            {Array.from({ length: tabs }).map((_, i) => <Skeleton key={i} height={18} width={110} />)}
          </div>
        </div>
      </div>
    )}
  </>
);

// Uma caixa com a mesma altura e forma dos campos (<Field>)
const FieldBox = ({ style }) => <Skeleton height={60} radius={8} style={style} />;

/**
 * Páginas com formulário de pesquisa (Transfers, Chauffeur, Car Rental): banner, separadores e
 * o cartão do formulário com as caixas dos campos nas mesmas posições.
 * rows: campos por linha, ex. [3, 2, 1]; "full" = um campo a toda a largura.
 */
export const SkeletonFormPage = ({ rows = [3, 2, 1], tabs = 2 }) => (
  <div>
    <HeroAndTabs tabs={tabs} />
    <div className="at-page-landing">
      <div className="at-card card p-4">
        <Skeleton height={28} width={240} className="mb-4" />
        {rows.map((row, r) => {
          const count = row === "full" ? 1 : row;
          const col = row === "full" ? "col-12" : count === 1 ? "col-md-4" : `col-md-${12 / count}`;
          return (
            <div key={r} className="row g-2 mb-2">
              {Array.from({ length: count }).map((_, i) => <div key={i} className={col}><FieldBox /></div>)}
            </div>
          );
        })}
        <div className="d-flex justify-content-end mt-3">
          <Skeleton height={38} width={120} radius={6} />
        </div>
      </div>
    </div>
  </div>
);

/** Tours: banner, separadores e a grelha de cartões (imagem, meta, título, texto, preço). */
export const SkeletonCardGrid = ({ cards = 6 }) => (
  <div>
    <HeroAndTabs />
    <div className="at-page-landing">
      <div className="at-tour-grid">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="at-tour-card">
            <Skeleton height={190} radius={0} />
            <div className="at-tour-card__body">
              <Skeleton height={11} width="55%" className="mb-2" />
              <Skeleton height={18} width="80%" className="mb-3" />
              <SkeletonText lines={4} />
              <Skeleton height={20} width="50%" className="mt-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** Compatibilidade: páginas antigas com formulário de pesquisa. */
export const SkeletonListPage = () => <SkeletonFormPage />;

export default Skeleton;
